import { css } from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { SIDENAV_THEME_TOKENS } from "../constants/sideNav";

/**
 * Foreground colour for hovered nav items (label text + icons).
 * In dark mode the hover pill is a mid-grey where the brand blue (linkText)
 * fails contrast, so hovered items go white (bodyText). In light mode they keep
 * the brand-blue affordance on the light hover pill.
 */
export const hoverForeground = (props) =>
  props.theme && props.theme.isDark
    ? themeGet("colors.bodyText")(props)
    : themeGet("colors.linkText")(props);

/**
 * Shared hover styles for all SideNav menu items
 * Provides consistent grey background and primary color on hover
 * Works for both collapsed and expanded states
 */
const sharedNavItemHoverStyles = css`
  &:hover:not(.active),
  &:focus:not(.active),
  &:active:not(.active) {
    outline: none;
    background-color: ${themeGet("colors.hoverSurface")};
    color: ${hoverForeground};
  }

  path {
    transition: ${themeGet("transition.transitionDefault")};
  }

  &:hover:not(.active) path,
  &:focus:not(.active) path,
  &:active:not(.active) path {
    fill: ${hoverForeground};
  }
`;

/**
 * Shared active state styles for all SideNav menu items
 * Provides consistent primaryLightest background and greyDarkest text color
 * Works with className-based active state (e.g., BaseSection links)
 */
export const sharedNavItemActiveStyles = css`
  &.active {
    background-color: ${themeGet("colors.navActiveBackground")};
    color: ${themeGet("colors.bodyText")};
  }

  &.active path {
    fill: ${themeGet("colors.bodyText")};
  }
`;

/**
 * Shared active state styles using props (for NavItem component)
 * Provides consistent primaryLightest background and greyDarkest text color
 * Works with prop-based active state
 */
export const sharedNavItemActiveStylesProps = (props) => {
  if (!props.active) return "";
  return css`
    background-color: ${themeGet("colors.navActiveBackground")};
    color: ${themeGet("colors.bodyText")};
    path {
      fill: ${themeGet("colors.bodyText")};
    }
  `;
};

/**
 * Shared base styles for all SideNav menu items
 * Common transition, border-radius, and color patterns
 */
export const sharedNavItemBaseStyles = css`
  transition: ${themeGet("transition.transitionDefault")};
  border-radius: ${themeGet("radii.2")};
  color: ${themeGet("colors.bodyText")};
`;

/**
 * Shared container styles for ALL menu item wrappers
 * Provides consistent sizing, padding, and hover behavior.
 * Use on all Item/li/wrapper components across NavItem and BaseSection.
 *
 * Key features:
 * - Theme tokens for spacing (sizes.l, space.xs) and border-radius
 * - Grey hover background for non-active items (desktop only)
 * - Blue text and icons on hover (desktop)
 * - Blue background for active items (consumers may override for expandable items; see NavItem SideNavItemWrapper)
 */
export const sharedMenuItemContainerStyles = css`
  border-radius: ${themeGet(`radii.${SIDENAV_THEME_TOKENS.BORDER_RADIUS}`)};
  width: auto;
  display: flex;
  align-items: center;

  /* Desktop only hover behavior */
  @media (hover: hover) and (pointer: fine) {
    /* Grey hover background for non-active items */
    &:hover:not(.active):not(:has(a.active)):not(:has(button.active)) {
      background-color: ${themeGet("colors.hoverSurface")};
    }

    /* Blue text and icons on ALL hover states (active or not) */
    &:hover,
    &:has(a.active):hover,
    &:has(button.active):hover {
      a,
      button:not(.nav-item-trailing),
      .nav-item-text {
        color: ${hoverForeground} !important;
      }

      path {
        fill: ${hoverForeground};
      }

      path[stroke] {
        stroke: ${hoverForeground};
      }
    }
  }

  /* Blue background for active items (overridden by NavItem for expandable items) */
  &.active,
  &:has(a.active),
  &:has(button.active) {
    background-color: ${themeGet("colors.navActiveBackground")};
  }
`;

/**
 * Shared link/button styles for menu items
 * Provides consistent text sizing, spacing, and color
 * Use on anchor or button elements inside containers
 * Note: Hover behavior is controlled by parent container, not individual links
 */
export const sharedMenuItemInnerStyles = css`
  display: flex;
  gap: ${themeGet("space.s")};
  align-items: center;
  padding: 0;
  flex: 1;
  width: 100%;
  min-height: ${themeGet(`sizes.${SIDENAV_THEME_TOKENS.MENU_ITEM_HEIGHT}`)};
  text-decoration: none;
  font-size: ${themeGet("fontSizes.1")};
  color: ${themeGet("colors.bodyText")};
  background-color: transparent;
  border: none;
`;

/**
 * Shared styles for pinned items (teams/people with avatars)
 * These items are taller than regular menu items to accommodate avatars
 * Use this for custom pinned item implementations in consuming apps
 * Uses theme tokens: sizes.xl (32px) for height, space.xxs (2px) for gap
 */
export const sharedPinnedItemContainerStyles = css`
  padding: ${themeGet(`space.${SIDENAV_THEME_TOKENS.ITEM_PADDING}`)};
  border-radius: ${themeGet(`radii.${SIDENAV_THEME_TOKENS.BORDER_RADIUS}`)};
  min-height: ${themeGet(`sizes.${SIDENAV_THEME_TOKENS.PINNED_ITEM_HEIGHT}`)};
  height: ${themeGet(`sizes.${SIDENAV_THEME_TOKENS.PINNED_ITEM_HEIGHT}`)};
  max-height: ${themeGet(`sizes.${SIDENAV_THEME_TOKENS.PINNED_ITEM_HEIGHT}`)};
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  gap: ${themeGet(`space.${SIDENAV_THEME_TOKENS.ITEM_CONTENT_GAP}`)};

  /* Grey hover background for non-active items - desktop only */
  @media (hover: hover) and (pointer: fine) {
    &:hover:not(.active) {
      background-color: ${themeGet("colors.hoverSurface")};
      color: ${hoverForeground};

      a {
        color: ${hoverForeground};
      }
    }

    /* For active items, turn text to the hover foreground on hover, but DON'T
       change avatar/icon colors */
    &.active:hover {
      a {
        color: ${hoverForeground};
      }
    }
  }

  /* Blue background for active items */
  &.active {
    background-color: ${themeGet("colors.navActiveBackground")};
  }
`;

export default sharedNavItemHoverStyles;
